/**
 * PullQuote — amplifies a key line from long-form prose (used heavily on the
 * About / Lingua Franca page). Tokens + Tailwind only.
 *
 * Two variants:
 *   default — a left-accented editorial serif-italic call-out for a short line.
 *   lead    — a centred, full-paragraph "excerpt" that opens a card: a scannable
 *             gist a reader can skim before (or instead of) the full prose.
 *             Centred and capped to the reading measure so the lines stay legible
 *             while the body prose around it spans the full card width.
 */
import type { ReactNode } from 'react';
import { cn } from '../../utils';
import { inlineFormat } from './Prose';

export interface PullQuoteProps {
  readonly text: ReactNode;
  readonly author?: string;
  readonly variant?: 'default' | 'lead';
}

// Inline emphasis inside a quote (**bold**, *italic*, [link]) — styled to match
// the way Prose renders the same markers (semibold foreground / editorial accent).
const EMPHASIS =
  '[&_strong]:font-semibold [&_strong]:text-foreground [&_em]:font-editorial [&_em]:text-primary [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4';

export function PullQuote({ text, author, variant = 'default' }: PullQuoteProps) {
  // A string may carry inline markdown — render it through the shared inlineFormat
  // so emphasis renders instead of leaking literal asterisks. A non-string
  // ReactNode (already-built JSX) is rendered as-is.
  const body =
    typeof text === 'string'
      ? <span dangerouslySetInnerHTML={{ __html: inlineFormat(text) }} />
      : text;

  if (variant === 'lead') {
    return (
      <blockquote className="mx-auto max-w-(--reading-measure) border-t border-primary/40 pt-5 text-center">
        <p className={cn('text-lg leading-relaxed text-foreground sm:text-xl', EMPHASIS)}>{body}</p>
        {author && <footer className="mt-3 text-sm text-muted-foreground">— {author}</footer>}
      </blockquote>
    );
  }
  return (
    <blockquote className="border-l-4 border-primary pl-6">
      <p className={cn('font-editorial text-3xl italic leading-snug text-foreground sm:text-4xl', EMPHASIS)}>{body}</p>
      {author && <footer className="mt-3 text-sm text-muted-foreground">— {author}</footer>}
    </blockquote>
  );
}
